import { ReactNode, useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Server as ServerIcon } from "lucide-react";

export type Tone = "default" | "ok" | "warn" | "bad" | "accent" | "info";

export function PageHead({
  title,
  subtitle,
  right,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {right && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{right}</div>}
    </div>
  );
}

export function StatTile({
  label,
  value,
  sub,
  tone,
  icon,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  tone?: "good" | "warn" | "bad";
  icon?: ReactNode;
}) {
  return (
    <div className="stat-card">
      <div className="stat-card__head">
        <div className="stat-card__label">{label}</div>
        {icon && (
          <div className={"stat-card__icon" + (tone ? " stat-card__icon--" + tone : "")}>
            {icon}
          </div>
        )}
      </div>
      <div className={"stat-card__value" + (tone ? " stat-card__value--" + tone : "")}>
        {value}
      </div>
      {sub && <div className="stat-card__sub">{sub}</div>}
    </div>
  );
}

export function Section({
  title,
  subtitle,
  right,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="section-card">
      <div className="section-card__head">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export function Pill({
  children,
  tone = "default",
  mono = true,
}: {
  children: ReactNode;
  tone?: Tone;
  mono?: boolean;
}) {
  return (
    <span className={"pill pill--" + tone + (mono ? " pill--mono" : "")}>{children}</span>
  );
}

export function StatusPill({ online }: { online: boolean }) {
  return (
    <span className={"st-pill st-pill--" + (online ? "ok" : "bad")}>
      <span className="st-pill__dot" />
      {online ? "online" : "offline"}
    </span>
  );
}

export function MetricPills<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { key: T; label: string; icon?: ReactNode }[];
  active: T;
  onChange: (k: T) => void;
}) {
  return (
    <div className="metric-pills">
      {tabs.map((t) => (
        <button
          key={t.key}
          className={"metric-pill" + (active === t.key ? " active" : "")}
          onClick={() => onChange(t.key)}
        >
          {t.icon}
          {t.label}
        </button>
      ))}
    </div>
  );
}

export interface HostOption {
  hostname: string;
  ip_address?: string | null;
  os?: string | null;
  online?: boolean;
}

export function HostPicker({
  hosts,
  value,
  onChange,
}: {
  hosts: HostOption[];
  value: string | null;
  onChange: (h: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const host = hosts.find((h) => h.hostname === value) || hosts[0];
  if (!host) return null;

  return (
    <div className="host-pill-row">
      <div className="host-picker" ref={ref}>
        <button className="host-pill host-pill--btn" onClick={() => setOpen((v) => !v)}>
          <div className="host-pill__ic">
            <ServerIcon size={14} />
          </div>
          <div>
            <b>{host.hostname}</b>
            <span>
              {host.ip_address || "—"}
              {" · "}
              <StatusPill online={!!host.online} />
            </span>
          </div>
          <span className={"host-pill__chev" + (open ? " open" : "")}>
            <ChevronDown size={14} />
          </span>
        </button>
        {open && (
          <div className="host-picker__menu">
            {hosts.map((s) => (
              <button
                key={s.hostname}
                className={"host-picker__item" + (s.hostname === host.hostname ? " active" : "")}
                onClick={() => {
                  onChange(s.hostname);
                  setOpen(false);
                }}
              >
                <span className="host-picker__check">
                  {s.hostname === host.hostname ? <Check size={14} /> : null}
                </span>
                <span className="host-picker__ic">
                  <ServerIcon size={14} />
                </span>
                <span className="host-picker__name">{s.hostname}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function FilterTabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: T[];
  active: T;
  onChange: (t: T) => void;
}) {
  return (
    <div className="filter-tabs">
      {tabs.map((t) => (
        <div
          key={t}
          className={"filter-tab" + (active === t ? " active" : "")}
          onClick={() => onChange(t)}
        >
          {t}
        </div>
      ))}
    </div>
  );
}
